import React, { useEffect, useRef, useState } from 'react';

// Canvas-based floating hexagon background with scroll + pointer parallax
// Default to a subtle but visible light gray tone
// If `count` is not provided, particle density scales with container area.
const FloatingHexagonBackground = ({ count, color = { hue: 210, sat: 12, light: 65 } }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);
  const sizeRef = useRef({ width: 0, height: 0 });
  const scrollRef = useRef({ current: 0, target: 0 });
  const [enabled, setEnabled] = useState(() => (typeof window === 'undefined' ? true : window.innerWidth >= 1024));

  useEffect(() => {
    if (typeof window === 'undefined') return () => {};
    const checkEnabled = () => setEnabled(window.innerWidth >= 1024);
    checkEnabled();
    window.addEventListener('resize', checkEnabled);
    return () => window.removeEventListener('resize', checkEnabled);
  }, []);

  useEffect(() => {
    if (!enabled) {
      // Ensure any running animation frame is cancelled when disabling
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      particlesRef.current = [];
      return () => {};
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let dpr = 1;
    const getContainer = () => {
      const overlay = canvas.parentElement || document.body;
      return overlay.offsetParent || overlay.parentElement || overlay || document.body;
    };
    const computeTargetCount = (w, h, pixelRatio) => {
      if (count != null) return count;
      const area = Math.max(1, w * h);
      const baseDensity = 1 / 15000; // ~133 per 2Mpx
      const dprFactor = Math.max(1, Math.sqrt(pixelRatio || 1));
      const raw = Math.round((area * baseDensity) / dprFactor);
      const capped = Math.max(120, Math.min(300, raw));
      if (w < 1024) {
        return Math.min(80, capped);
      }
      return capped;
    };

    const resize = () => {
      const container = getContainer();
      const width = container.clientWidth || window.innerWidth;
      const height = container.scrollHeight || container.offsetHeight || window.innerHeight;
      dpr = width < 1024 ? 1 : Math.min(window.devicePixelRatio || 1, 1.5);
      sizeRef.current.width = width;
      sizeRef.current.height = height;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const desired = computeTargetCount(width, height, dpr);
      if (!particlesRef.current.length || Math.abs(particlesRef.current.length - desired) > Math.max(10, Math.round(desired * 0.1))) {
        initParticles(desired);
      }
    };

    const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const initParticles = (n) => {
      const { width, height } = sizeRef.current;
      const items = new Array(n).fill(0).map(() => {
        const depth = 0.6 + Math.random() * 2.2;
        const size = 4 + Math.random() * 42;
        // Lower base drift for calm free-roam motion
        const speed = 0.04 + Math.random() * 0.12; // base speed in px/frame @60fps
        const angle = Math.random() * Math.PI * 2;
        return {
          baseX: Math.random() * width,
          baseY: Math.random() * height,
          x: 0,
          y: 0,
          vx: (Math.cos(angle) * speed) / depth,
          vy: (Math.sin(angle) * speed) / depth,
          size,
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.01,
          depth,
          // Free-roam wander parameters
          wanderAngle: Math.random() * Math.PI * 2,
          wanderRate: 0.001 + Math.random() * 0.003, // rad/frame @60fps (slower heading change)
          wanderStrength: 2 + Math.random() * 5, // px/s^2 (gentler acceleration)
          // Visual tuning: increase visibility while staying subtle
          strokeAlpha: 0.01 + Math.random() * 0.04, // 0.16–0.32
          fillAlpha: Math.random() < 0.5 ? (0.04 + Math.random() * 0.045) : 0, // 0.05–0.13
          strokeWidth: 0.9 + Math.random() * 1.1, // 0.9–2.0
        };
      });
      // Initialize current positions to base
      for (const p of items) {
        p.x = p.baseX;
        p.y = p.baseY;
      }
      particlesRef.current = items;
    };

    const onScroll = () => {
      scrollRef.current.target = window.scrollY || window.pageYOffset || 0;
    };

    window.addEventListener('resize', resize);
    // Observe container resize to follow dynamic content height
    const container = getContainer();
    let ro;
    if ('ResizeObserver' in window && container) {
      ro = new ResizeObserver(() => resize());
      ro.observe(container);
    }
    window.addEventListener('scroll', onScroll, { passive: true });

    resize();
    // Ensure particle set exists even if resize didn't trigger init above
    if (!particlesRef.current.length) {
      initParticles(computeTargetCount(sizeRef.current.width, sizeRef.current.height, dpr));
    }
    scrollRef.current.current = window.scrollY || 0;

    let lastTime = performance.now();
    const frameInterval = 1000 / 30; // throttle physics to ~30fps to reduce CPU further

    const drawHexagon = (x, y, r, rot, strokeStyle, lineWidth, fillStyle) => {
      const sides = 6;
      const step = (Math.PI * 2) / sides;
      ctx.beginPath();
      for (let i = 0; i <= sides; i++) {
        const a = rot + i * step;
        const px = x + Math.cos(a) * r;
        const py = y + Math.sin(a) * r;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      if (fillStyle) {
        ctx.fillStyle = fillStyle;
        ctx.fill();
      }
      ctx.strokeStyle = strokeStyle;
      ctx.lineWidth = lineWidth;
      ctx.stroke();
    };

    const animate = (time) => {
      const deltaMs = time - lastTime;
      if (deltaMs < frameInterval) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      const dt = Math.min(deltaMs / 1000, 0.05); // seconds, cap dt for stability
      lastTime = time;

      const { width, height } = sizeRef.current;
      ctx.clearRect(0, 0, width, height);

      // Smooth scroll target only (no pointer interaction)
      scrollRef.current.current += (scrollRef.current.target - scrollRef.current.current) * 0.08;

      const scrollParallax = scrollRef.current.current * 0.05; // px

      const hue = color.hue ?? 210;
      const sat = color.sat ?? 12;
      const light = color.light ?? 65;

      const prts = particlesRef.current;
      const friction = 0.987; // damping

      for (let i = 0; i < prts.length; i++) {
        const p = prts[i];
        const depthFactor = 1 / p.depth;

        // Free roam: no spring toward base — start with no force
        let fx = 0;
        let fy = 0;

        // Smooth directional wander (random walk with memory)
        p.wanderAngle += (Math.random() - 0.5) * p.wanderRate * 60 * dt;
        fx += Math.cos(p.wanderAngle) * p.wanderStrength * depthFactor;
        fy += Math.sin(p.wanderAngle) * p.wanderStrength * depthFactor;

        // Integrate velocity with damping and light ambient noise
        p.vx = (p.vx + fx * dt) * friction;
        p.vy = (p.vy + fy * dt) * friction;
        if (!prefersReduced) {
          // Tiny nudges to avoid perfectly straight lines
          p.vx += (Math.random() - 0.5) * 0.004 * depthFactor;
          p.vy += (Math.random() - 0.5) * 0.004 * depthFactor;
          p.rotation += p.rotationSpeed * dt * 60;
        }

        // Clamp speeds
        const maxSpeed = 0.6 * depthFactor; // 90% reduction for ultra-slow motion
        const vmag = Math.hypot(p.vx, p.vy);
        if (vmag > maxSpeed) {
          const s = maxSpeed / vmag;
          p.vx *= s;
          p.vy *= s;
        }

        // Update position
        p.x += p.vx * dt * 60;
        p.y += p.vy * dt * 60;

        // Bounds: toroidal wrap to keep the field filled without anchoring
        const m = 12;
        if (p.x < -m) p.x = width + m;
        if (p.x > width + m) p.x = -m;
        if (p.y < -m) p.y = height + m;
        if (p.y > height + m) p.y = -m;

        // Render with only scroll parallax
        const rx = p.x;
        const ry = p.y + scrollParallax * depthFactor;

        const stroke = `hsla(${hue}, ${sat}%, ${light}%, ${p.strokeAlpha})`;
        // Slightly lighter fill than stroke for subtlety
        const fillL = Math.min(92, light + 10);
        const fill = p.fillAlpha > 0 ? `hsla(${hue}, ${sat}%, ${fillL}%, ${p.fillAlpha})` : null;

        drawHexagon(rx, ry, p.size, p.rotation, stroke, p.strokeWidth, fill);
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', onScroll);
      if (ro) ro.disconnect();
      cancelAnimationFrame(animationRef.current);
    };
  }, [count, color.hue, color.sat, color.light, enabled]);

  if (!enabled) {
    return null;
  }

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      aria-hidden="true"
    />
  );
};

export default FloatingHexagonBackground;
